import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Icon,
  IconButton,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { format, isToday } from "date-fns";
import { Fragment, useState } from "react";
import { useIntl } from "react-intl";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Rectangle,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  ChartDateType,
  CurtailmentQuarter,
  EarningsBucket,
  EarningsDay,
  EarningsGrouping,
  EarningsHour,
  EarningsSummary,
  useGetCurtailment,
  useGetEarnings,
  useGetEarningsSummary,
  useGetStrategy,
} from "api";
import { FiltersContextProvider, useFiltersContext } from "components/contexts";
import { DatePickerChips, FiltersWidget } from "components/elements/filters";
import { StickyTable } from "components/elements/mui";
import { CurtailmentOutlook, EarningsWidget } from "components/elements/widgets";
import { formatKwh, formatPln } from "components/elements/widgets/Earnings/format";
import { PageLayout } from "components/layouts";
import { useFormatMessage } from "lang";
import { getSelectedPowerPlant, useAppSelector } from "store";
import { pulseSx } from "styles/animations";
import { priceSeriesColors } from "styles/theme";
import { useChartNavigation } from "utils/hooks/useChartNavigation";

// Headline figures. `primary` ones (what was earned, what was produced) are
// large; the rest stay small and grey so the eye lands on the two that matter.
// Summary tiles: two per row on phones, four on anything wider — a row of
// four must never break into three and one.
const tileGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
  gap: 2,
  alignItems: "end",
} as const;

const Summary = ({
  label,
  value,
  unit = "PLN",
  color,
  primary = false,
  neutral = false,
}: {
  label: string;
  value: number | null;
  unit?: string;
  color?: string;
  primary?: boolean;
  // A negative that is a direction, not a loss: the energy settled at CRO is
  // below zero where the plant fell short of PO on FIX1 days.
  neutral?: boolean;
}) => {
  const intl = useIntl();
  const text =
    value === null ? "—" : unit === "PLN" ? formatPln(intl, value) : formatKwh(intl, value);
  return (
    <Stack minWidth={0} flex={primary ? "1 1 140px" : "1 1 110px"}>
      <Typography
        variant="caption"
        fontWeight={600}
        letterSpacing={0.5}
        noWrap
        sx={{ color: color ?? "text.secondary", opacity: primary || color ? 1 : 0.8 }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="baseline" gap={0.5}>
        <Typography
          variant={primary ? "h5" : "body1"}
          fontWeight={primary ? 700 : 500}
          lineHeight={1.1}
          color={!neutral && value !== null && value < 0 ? "error.main" : primary ? "text.primary" : "text.secondary"}
        >
          {text}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {unit}
        </Typography>
      </Stack>
    </Stack>
  );
};

// The tooltip every chart in the app uses: the period in the header with
// arrows to step through the bars, one row per series. `rows` is what to
// list for the record under the cursor (or the locked one).
interface TooltipRow {
  label: string;
  value: number | null;
  color?: string;
  muted?: boolean;
}

const EarningsTooltip = <T,>({
  active,
  payload,
  lockedIndex,
  allData,
  onPrev,
  onNext,
  formatLabel,
  rows,
}: {
  active?: boolean;
  payload?: { payload: T }[];
  lockedIndex: number | null;
  allData: T[] | undefined;
  onPrev: () => void;
  onNext: () => void;
  formatLabel: (record: T) => string;
  rows: (record: T) => TooltipRow[];
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const isLocked = lockedIndex !== null;
  const hovered = payload?.[0]?.payload;
  if (!isLocked && (!active || !hovered)) return null;
  const record = isLocked ? allData?.[lockedIndex] : hovered;
  if (!record) return null;
  const canGoPrev = isLocked ? lockedIndex > 0 : true;
  const canGoNext = isLocked ? lockedIndex < (allData?.length ?? 1) - 1 : true;

  return (
    <Paper
      elevation={0}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      sx={{
        m: 1,
        border: "solid",
        borderWidth: 2,
        borderColor: "divider",
        backgroundColor:
          theme.palette.mode === "dark" ? "rgba(18, 18, 18, 0.85)" : "rgba(255, 255, 255, 0.95)",
        minWidth: 220,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{ borderBottom: "solid", borderColor: "divider", borderWidth: "0 0 1px 0", px: 0.5, py: 0.5 }}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          disabled={!canGoPrev}
          sx={{ p: 0.25 }}
        >
          <Icon sx={{ fontSize: 18 }}>chevron_left</Icon>
        </IconButton>
        <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 600 }}>{formatLabel(record)}</Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          disabled={!canGoNext}
          sx={{ p: 0.25 }}
        >
          <Icon sx={{ fontSize: 18 }}>chevron_right</Icon>
        </IconButton>
      </Stack>
      <Stack gap={0.5} sx={{ p: 1.5 }}>
        {rows(record).map((r) => (
          <Stack key={r.label} direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 0.5,
                backgroundColor: r.color ?? "transparent",
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{ flex: 1, color: r.muted ? "text.secondary" : "text.primary" }}
            >
              {r.label}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={r.muted ? 400 : 600}
              sx={{
                whiteSpace: "nowrap",
                color: r.value !== null && r.value < 0 ? "error.main" : r.muted ? "text.secondary" : "text.primary",
              }}
            >
              {r.value === null ? "—" : `${formatPln(intl, r.value, 2)} PLN`}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

interface HourBar {
  x: number; // hour index + 0.5: the bar sits between its two hour ticks
  hour: EarningsHour;
  rdn: number | null;
  pse: number | null;
  total: number | null;
  tradea: number | null;
}

// Hours on a numeric axis with a tick at every hour boundary, so a bar spans
// exactly the hour it pays for instead of straddling a label.
const EarningsChart = ({ day }: { day: EarningsDay }) => {
  const theme = useTheme();
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { pse: pseColor, pseSettled: tradeaColor } = priceSeriesColors(theme.palette.mode);
  const rdnColor = theme.palette.primary.main;
  const hasTradea = day.hours.some((h) => h.tradeaPln !== null);
  const rows: HourBar[] = day.hours.map((h, i) => ({
    x: i + 0.5,
    hour: h,
    rdn: h.rdnPln,
    pse: h.psePln,
    total: h.totalPln,
    tradea: h.tradeaPln,
  }));
  const n = day.hours.length;
  const ticks = Array.from({ length: n + 1 }, (_, i) => i);
  // Labels every three hours; the two day edges stay unlabelled like the
  // other day charts.
  const tickLabel = (i: number) => (i % 3 !== 0 || i === 0 || i >= n ? "" : day.hours[i].time);
  const period = (r: HourBar) => {
    const i = Math.floor(r.x);
    return `${r.hour.time} - ${i + 1 < n ? day.hours[i + 1].time : "24:00"}`;
  };

  const { lockedIndex, chartEventHandlers, tooltipProps, navigationProps, renderIndicators } =
    useChartNavigation<HourBar>({
      data: rows,
      dataKey: "total",
      seriesType: "bar",
      seriesColor: rdnColor,
      theme,
      getX: (r) => r.x,
    });

  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart
        data={rows}
        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        stackOffset="sign"
        barCategoryGap="12%"
        {...chartEventHandlers}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
        <XAxis
          dataKey="x"
          type="number"
          domain={[0, n]}
          ticks={ticks}
          interval={0}
          tickLine={false}
          tickFormatter={tickLabel}
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
        />
        <YAxis
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          width={48}
          tickFormatter={(v: number) => formatPln(intl, v)}
        />
        <Tooltip
          position={{ y: 0 }}
          // Future hours have no earnings yet but carry the expectations;
          // recharts would otherwise hide the tooltip for them.
          filterNull={false}
          {...tooltipProps}
          content={
            <EarningsTooltip<HourBar>
              {...navigationProps}
              formatLabel={period}
              rows={(r) => [
                { label: formatMessage("earnings.rdnPart"), value: r.rdn, color: rdnColor },
                { label: formatMessage("earnings.psePart"), value: r.pse, color: pseColor },
                { label: formatMessage("earnings.ourEstimate"), value: r.total },
                ...(hasTradea
                  ? [{ label: formatMessage("earnings.tradeaReport"), value: r.tradea, color: tradeaColor }]
                  : []),
                { label: formatMessage("earnings.tradeaForecast"), value: r.hour.expectedTradeaPln, muted: true },
                { label: formatMessage("earnings.ourForecast"), value: r.hour.expectedOursPln, muted: true },
              ]}
            />
          }
        />
        <Legend content={<PlainLegend />} />
        <ReferenceLine y={0} stroke={theme.palette.text.disabled} />
        {renderIndicators()}
        <Bar
          dataKey="rdn"
          name={formatMessage("earnings.rdnPart")}
          stackId="pln"
          fill={rdnColor}
          activeBar={<Rectangle stroke={theme.palette.text.primary} strokeWidth={1} />}
          activeIndex={lockedIndex ?? undefined}
          isAnimationActive={false}
        />
        <Bar
          dataKey="pse"
          name={formatMessage("earnings.psePart")}
          stackId="pln"
          fill={pseColor}
          activeBar={<Rectangle stroke={theme.palette.text.primary} strokeWidth={1} />}
          activeIndex={lockedIndex ?? undefined}
          isAnimationActive={false}
        />
        {hasTradea && (
          <Line
            type="monotone"
            dataKey="tradea"
            name={formatMessage("earnings.tradeaReport")}
            stroke={tradeaColor}
            strokeWidth={2}
            dot={false}
            activeDot={lockedIndex !== null ? false : undefined}
            connectNulls={false}
            isAnimationActive={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// Legend swatches: a square for bars, a line for the report.
const PlainLegend = ({
  payload,
}: {
  payload?: { value: string; color?: string; type?: string }[];
}) => (
  <Box
    component="ul"
    sx={{
      listStyle: "none",
      m: 0,
      p: 0,
      pt: 0.5,
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      columnGap: 1.5,
      rowGap: 0.25,
      fontSize: 12,
      color: "text.secondary",
    }}
  >
    {(payload ?? []).map((item) => (
      <Box component="li" key={item.value} sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
        {item.type === "line" ? (
          <svg width={22} height={6} aria-hidden>
            <line x1={0} y1={3} x2={22} y2={3} stroke={item.color} strokeWidth={2} />
          </svg>
        ) : (
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, backgroundColor: item.color }} />
        )}
        <span style={{ color: item.color }}>{item.value}</span>
      </Box>
    ))}
  </Box>
);

const money = (intl: ReturnType<typeof useIntl>, v: number | null) =>
  v === null ? "—" : formatPln(intl, v, 2);

// Table cells: the two columns that matter (production, total) in full
// colour and weight, everything else quieter.
const quiet = { color: "text.secondary", fontSize: "0.8rem" } as const;

const EarningsTable = ({ day }: { day: EarningsDay }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const hasTradea = day.hours.some((h) => h.tradeaPln !== null);
  // Hours unfolded into their four quarters, by row index (the quarters
  // array is built four per hour, so the 25-hour October day stays right).
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const cell = (h: EarningsHour, v: number | null, primary = false) => (
    <TableCell align="right" sx={primary ? undefined : quiet}>
      <Box
        component="span"
        sx={{
          fontWeight: primary ? 700 : undefined,
          color: v !== null && v < 0 ? "error.main" : undefined,
          // Hours priced with the nowcast are guesses; they pulse like the
          // predicted prices do.
          ...(h.croSource === "forecast" ? pulseSx() : {}),
        }}
      >
        {money(intl, v)}
      </Box>
    </TableCell>
  );
  const head = (label: string, primary = false) => (
    <TableCell align="right" sx={primary ? { fontWeight: 700 } : quiet}>
      {label}
    </TableCell>
  );
  const kwh = (v: number | null, primary = false) => (
    <TableCell align="right" sx={primary ? { fontWeight: 700 } : quiet}>
      {v === null ? "—" : formatKwh(intl, v)}
    </TableCell>
  );

  return (
    <StickyTable>
        <TableHead>
          <TableRow sx={{ "& th": { whiteSpace: "nowrap" } }}>
            <TableCell>{formatMessage("prices.tableTime")}</TableCell>
            {head(formatMessage("earnings.ourEstimate"), true)}
            {head(formatMessage("earnings.rdnPart"))}
            {head(formatMessage("earnings.psePart"))}
            {head(`${formatMessage("earnings.production")} kWh`, true)}
            {/* On a CRO day nothing is sold day-ahead for the plant: PO is only Tradea's forecast. */}
            {head(formatMessage(day.index === "CRO" ? "earnings.tradeaPoKwh" : "earnings.tradeaForecastKwh"))}
            {head(formatMessage("earnings.ourForecastKwh"))}
            {head(formatMessage("prices.rdn"))}
            {head(formatMessage("prices.pse"))}
            {hasTradea && head(formatMessage("earnings.tradeaReport"))}
            {head(formatMessage("earnings.tradeaForecastPln"))}
            {head(formatMessage("earnings.ourForecastPln"))}
          </TableRow>
        </TableHead>
        <TableBody>
          {day.hours.map((h, i) => (
            <Fragment key={`${i}-${h.time}`}>
              <TableRow
                hover
                onClick={() => toggle(i)}
                sx={{ cursor: "pointer", "& td": open.has(i) ? { borderBottom: 0 } : undefined }}
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Stack direction="row" alignItems="center" gap={0.25}>
                    <Icon sx={{ fontSize: 16, color: "text.secondary", transition: "transform 150ms", transform: open.has(i) ? "rotate(90deg)" : "none" }}>
                      chevron_right
                    </Icon>
                    {h.time}
                  </Stack>
                </TableCell>
                {cell(h, h.totalPln, true)}
                {cell(h, h.rdnPln)}
                {cell(h, h.psePln)}
                {kwh(h.producedKwh, true)}
                {kwh(h.forecastKwh)}
                {kwh(h.ourForecastKwh)}
                <TableCell align="right" sx={quiet}>{money(intl, h.fix1)}</TableCell>
                <TableCell align="right" sx={quiet}>{money(intl, h.cro)}</TableCell>
                {hasTradea && cell(h, h.tradeaPln)}
                <TableCell align="right" sx={quiet}>{money(intl, h.expectedTradeaPln)}</TableCell>
                <TableCell align="right" sx={quiet}>{money(intl, h.expectedOursPln)}</TableCell>
              </TableRow>
              {open.has(i) &&
                day.quarters.slice(i * 4, i * 4 + 4).map((q, j) => (
                  <TableRow
                    key={q.start}
                    sx={{
                      backgroundColor: "action.hover",
                      "& td": { fontSize: "0.75rem", py: 0.25, ...(j < 3 ? { borderBottom: 0 } : {}) },
                    }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap", pl: 4.5, color: "text.secondary" }}>{q.time}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: q.totalPln !== null && q.totalPln < 0 ? "error.main" : undefined }}>
                      <Box component="span" sx={q.croSource === "forecast" ? pulseSx() : undefined}>{money(intl, q.totalPln)}</Box>
                    </TableCell>
                    <TableCell align="right" sx={quiet}>{money(intl, q.rdnPln)}</TableCell>
                    <TableCell align="right" sx={{ ...quiet, color: q.psePln !== null && q.psePln < 0 ? "error.main" : quiet.color }}>{money(intl, q.psePln)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{q.producedKwh === null ? "—" : formatKwh(intl, q.producedKwh)}</TableCell>
                    <TableCell align="right" sx={quiet}>{q.forecastKwh === null ? "—" : formatKwh(intl, q.forecastKwh)}</TableCell>
                    <TableCell align="right" sx={quiet}>{q.ourForecastKwh === null ? "—" : formatKwh(intl, q.ourForecastKwh)}</TableCell>
                    <TableCell align="right" sx={quiet}>{money(intl, q.fix1)}</TableCell>
                    <TableCell align="right" sx={quiet}>
                      <Box component="span" sx={q.croSource === "forecast" ? pulseSx() : undefined}>{money(intl, q.cro)}</Box>
                    </TableCell>
                    {hasTradea && <TableCell />}
                    <TableCell align="right" sx={quiet}>{money(intl, q.expectedTradeaPln)}</TableCell>
                    <TableCell align="right" sx={quiet}>{money(intl, q.expectedOursPln)}</TableCell>
                  </TableRow>
                ))}
            </Fragment>
          ))}
          <TableRow sx={{ "& td": { fontWeight: 700, borderBottom: 0 } }}>
            <TableCell>{formatMessage("earnings.total")}</TableCell>
            <TableCell align="right" sx={day.totals.totalPln < 0 ? { color: "error.main" } : undefined}>
              {money(intl, day.totals.totalPln)}
            </TableCell>
            <TableCell align="right" sx={quiet}>{money(intl, day.totals.rdnPln)}</TableCell>
            <TableCell align="right" sx={quiet}>{money(intl, day.totals.psePln)}</TableCell>
            {kwh(day.totals.producedKwh, true)}
            {kwh(day.totals.forecastKwh)}
            {kwh(day.totals.ourForecastKwh)}
            <TableCell />
            <TableCell />
            {hasTradea && <TableCell align="right" sx={quiet}>{money(intl, day.totals.tradeaPln)}</TableCell>}
            <TableCell align="right" sx={quiet}>{money(intl, day.totals.expectedTradeaPln)}</TableCell>
            <TableCell align="right" sx={quiet}>{money(intl, day.totals.expectedOursPln)}</TableCell>
          </TableRow>
        </TableBody>
    </StickyTable>
  );
};

interface BucketBar {
  key: string;
  label: string; // axis label
  bucket: EarningsBucket;
  rdn: number;
  pse: number;
  total: number;
  tradea: number | null;
}

// Labels for a bucket on the axis and in the tooltip header.
const bucketLabels = (grouping: EarningsGrouping, b: EarningsBucket) => {
  const d = new Date(`${b.from}T12:00:00`);
  if (grouping === "month") return { axis: b.key.slice(8), full: format(d, "EEEE, d MMMM") };
  if (grouping === "year") return { axis: format(d, "LLL"), full: format(d, "LLLL yyyy") };
  return { axis: b.key, full: b.key };
};

// Month, year and total views: one bar per day, month or year, and the
// running difference against Tradea's own report for the days it reported.
const SummaryChart = ({ summary }: { summary: EarningsSummary }) => {
  const theme = useTheme();
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { pse: pseColor, pseSettled: tradeaColor } = priceSeriesColors(theme.palette.mode);
  const rdnColor = theme.palette.primary.main;
  const hasTradea = summary.buckets.some((b) => b.tradeaPln !== null);
  const rows: BucketBar[] = summary.buckets.map((b) => ({
    key: b.key,
    label: bucketLabels(summary.grouping, b).axis,
    bucket: b,
    rdn: b.rdnPln,
    pse: b.psePln,
    total: b.totalPln,
    tradea: b.tradeaPln,
  }));
  const { lockedIndex, chartEventHandlers, tooltipProps, navigationProps, renderIndicators } =
    useChartNavigation<BucketBar>({
      data: rows,
      dataKey: "total",
      seriesType: "bar",
      seriesColor: rdnColor,
      theme,
      getX: (r) => r.label,
    });
  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart
        data={rows}
        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        stackOffset="sign"
        barCategoryGap="12%"
        {...chartEventHandlers}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          interval={summary.grouping === "month" ? 1 : 0}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          width={52}
          tickFormatter={(v: number) => formatPln(intl, v)}
        />
        <Tooltip
          position={{ y: 0 }}
          {...tooltipProps}
          content={
            <EarningsTooltip<BucketBar>
              {...navigationProps}
              formatLabel={(r) => bucketLabels(summary.grouping, r.bucket).full}
              rows={(r) => [
                { label: formatMessage("earnings.rdnPart"), value: r.rdn, color: rdnColor },
                { label: formatMessage("earnings.psePart"), value: r.pse, color: pseColor },
                { label: formatMessage("earnings.ourEstimate"), value: r.total },
                ...(hasTradea
                  ? [
                      { label: formatMessage("earnings.tradeaReport"), value: r.tradea, color: tradeaColor },
                      {
                        label: formatMessage("earnings.difference"),
                        value: r.tradea === null ? null : Math.round((r.total - r.tradea) * 100) / 100,
                        muted: true,
                      },
                    ]
                  : []),
              ]}
            />
          }
        />
        <Legend content={<PlainLegend />} />
        <ReferenceLine y={0} stroke={theme.palette.text.disabled} />
        {renderIndicators()}
        <Bar
          dataKey="rdn"
          name={formatMessage("earnings.rdnPart")}
          stackId="pln"
          fill={rdnColor}
          activeBar={<Rectangle stroke={theme.palette.text.primary} strokeWidth={1} />}
          activeIndex={lockedIndex ?? undefined}
          isAnimationActive={false}
        />
        <Bar
          dataKey="pse"
          name={formatMessage("earnings.psePart")}
          stackId="pln"
          fill={pseColor}
          activeBar={<Rectangle stroke={theme.palette.text.primary} strokeWidth={1} />}
          activeIndex={lockedIndex ?? undefined}
          isAnimationActive={false}
        />
        {hasTradea && (
          <Line
            type="monotone"
            dataKey="tradea"
            name={formatMessage("earnings.tradeaReport")}
            stroke={tradeaColor}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={lockedIndex !== null ? false : undefined}
            connectNulls={false}
            isAnimationActive={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const SummaryTable = ({ summary }: { summary: EarningsSummary }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const hasTradea = summary.buckets.some((b) => b.tradeaPln !== null);
  // Production split the way it was paid (sold day-ahead at FIX1, the rest at
  // CRO) in place of Tradea's PO, which counted CRO days too (2026-10-19).
  const split = summary.totals.rdnKwh != null && summary.totals.pseKwh != null;
  const splitCells = (b: EarningsBucket) =>
    b.tariff != null || !b.hasProduction ? (
      <>
        <TableCell align="right" sx={quiet}>—</TableCell>
        <TableCell align="right" sx={quiet}>—</TableCell>
      </>
    ) : (
      <>
        <TableCell align="right" sx={quiet}>{formatKwh(intl, b.rdnKwh ?? null)}</TableCell>
        <TableCell align="right" sx={quiet}>{formatKwh(intl, b.pseKwh ?? null)}</TableCell>
      </>
    );
  const signed = (v: number | null, primary = false) => (
    <Box
      component="span"
      sx={{ color: v !== null && v < 0 ? "error.main" : undefined, fontWeight: primary ? 700 : undefined }}
    >
      {money(intl, v)}
    </Box>
  );
  const head = (label: string, primary = false) => (
    <TableCell align="right" sx={primary ? { fontWeight: 700 } : quiet}>
      {label}
    </TableCell>
  );
  // "Ours − Tradea" with its sign written out and no red: below zero is Tradea
  // having paid more than we estimated, which is not a loss.
  const difference = (v: number | null) =>
    v === null ? "—" : `${v > 0 ? "+" : ""}${money(intl, v)}`;
  const periodLabel = (b: EarningsBucket) =>
    summary.grouping === "month"
      ? b.key.slice(5)
      : summary.grouping === "year"
        ? format(new Date(`${b.from}T12:00:00`), "LLLL")
        : b.key;
  return (
    <StickyTable>
        <TableHead>
          <TableRow sx={{ "& th": { whiteSpace: "nowrap" } }}>
            <TableCell>{formatMessage("earnings.period")}</TableCell>
            {head(formatMessage("earnings.ourEstimate"), true)}
            {head(formatMessage("earnings.rdnPart"))}
            {head(formatMessage("earnings.psePart"))}
            {head(`${formatMessage("earnings.production")} kWh`, true)}
            {split ? (
              <>
                {head(formatMessage("earnings.rdnKwh"))}
                {head(formatMessage("earnings.pseKwh"))}
              </>
            ) : (
              head(formatMessage("earnings.tradeaForecastKwh"))
            )}
            <TableCell sx={quiet}>{formatMessage("earnings.settlement")}</TableCell>
            {hasTradea && (
              <>
                {head(formatMessage("earnings.tradeaReport"))}
                {head(formatMessage("earnings.difference"))}
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {summary.buckets.map((b) => (
            <TableRow key={b.key}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{periodLabel(b)}</TableCell>
              <TableCell align="right">
                <Box component="span" sx={b.settled ? undefined : pulseSx()}>{signed(b.totalPln, true)}</Box>
              </TableCell>
              <TableCell align="right" sx={quiet}>{signed(b.rdnPln)}</TableCell>
              <TableCell align="right" sx={quiet}>{signed(b.psePln)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {b.hasProduction ? formatKwh(intl, b.producedKwh) : "—"}
              </TableCell>
              {split ? splitCells(b) : <TableCell align="right" sx={quiet}>{formatKwh(intl, b.forecastKwh)}</TableCell>}
              <TableCell sx={quiet}>
                {/* A day at a contract rate has no FIX1/CRO choice at all — PV
                    Dom since 2025-05 — and used to read "PSE" for it. */}
                {b.index === null || b.mode === "fixed"
                  ? "—"
                  : b.index === "mixed"
                    ? formatMessage("earnings.mixed")
                    : b.index === "FIX1"
                      ? "RDN"
                      : "PSE"}
              </TableCell>
              {hasTradea && (
                <>
                  <TableCell align="right" sx={quiet}>{signed(b.tradeaPln)}</TableCell>
                  <TableCell align="right" sx={quiet}>
                    {b.tradeaPln === null ? "—" : difference(Math.round((b.totalPln - b.tradeaPln) * 100) / 100)}
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
          <TableRow sx={{ "& td": { fontWeight: 700, borderBottom: 0 } }}>
            <TableCell>{formatMessage("earnings.total")}</TableCell>
            <TableCell align="right">{signed(summary.totals.totalPln, true)}</TableCell>
            <TableCell align="right" sx={quiet}>{signed(summary.totals.rdnPln)}</TableCell>
            <TableCell align="right" sx={quiet}>{signed(summary.totals.psePln)}</TableCell>
            <TableCell align="right">{formatKwh(intl, summary.totals.producedKwh)}</TableCell>
            {split ? (
              <>
                <TableCell align="right" sx={quiet}>{formatKwh(intl, summary.totals.rdnKwh ?? null)}</TableCell>
                <TableCell align="right" sx={quiet}>{formatKwh(intl, summary.totals.pseKwh ?? null)}</TableCell>
              </>
            ) : (
              <TableCell align="right" sx={quiet}>{formatKwh(intl, summary.totals.forecastKwh)}</TableCell>
            )}
            <TableCell />
            {hasTradea && (
              <>
                <TableCell align="right" sx={quiet}>{signed(summary.totals.tradeaPln)}</TableCell>
                <TableCell align="right" sx={quiet}>{difference(summary.totals.diffPln)}</TableCell>
              </>
            )}
          </TableRow>
        </TableBody>
    </StickyTable>
  );
};

const Loading = () => (
  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
    <CircularProgress />
  </Box>
);

const SummaryView = ({ grouping, date }: { grouping: EarningsGrouping; date: Date }) => {
  const formatMessage = useFormatMessage();
  const intl = useIntl();
  const theme = useTheme();
  const { pse: pseColor } = priceSeriesColors(theme.palette.mode);
  const { data, isLoading } = useGetEarningsSummary(grouping, date);
  const summary = data?.configured ? data : null;
  if (isLoading) return <Loading />;
  if (!summary || summary.buckets.length === 0) {
    return (
      <Typography color="text.secondary" textAlign="center" py={6}>
        {formatMessage("prices.noData")}
      </Typography>
    );
  }
  return (
    <Stack gap={2}>
      {/* Tradea's own report sits next to our total: it is the number that
          gets paid, ours is the estimate of it. */}
      <Box sx={tileGridSx}>
        <Summary label={formatMessage("earnings.ourEstimate")} value={summary.totals.totalPln} primary />
        <Summary label={formatMessage("earnings.tradeaReport")} value={summary.totals.tradeaPln} primary />
        <Summary label={formatMessage("earnings.production")} value={summary.totals.producedKwh} unit="kWh" primary />
        {/* Production split the way it was paid: sold day-ahead at FIX1 and the
            rest at CRO, which add up to it — plus, where the range also holds
            days at a contract rate (PV Dom), what went at the rate. Until
            2026-10-19 this was Tradea's PO over every day, CRO days included. */}
        {summary.totals.rdnKwh != null && summary.totals.pseKwh != null ? (
          <>
            <Summary label={formatMessage("earnings.rdnKwh")} value={summary.totals.rdnKwh} unit="kWh" color={theme.palette.primary.main} />
            <Summary label={formatMessage("earnings.pseKwh")} value={summary.totals.pseKwh} unit="kWh" color={pseColor} neutral />
            {summary.buckets.some((b) => b.tariff != null) && (
              <Summary
                label={formatMessage("earnings.atRateKwh")}
                value={Math.round((summary.totals.producedKwh - summary.totals.rdnKwh - summary.totals.pseKwh) * 100) / 100}
                unit="kWh"
              />
            )}
          </>
        ) : (
          <Summary label={formatMessage("earnings.tradeaForecastKwh")} value={summary.totals.forecastKwh} unit="kWh" />
        )}
        <Summary label={formatMessage("earnings.rdnPart")} value={summary.totals.rdnPln} color={theme.palette.primary.main} />
        <Summary label={formatMessage("earnings.psePart")} value={summary.totals.psePln} color={pseColor} />
      </Box>
      {summary.totals.tradeaPln !== null ? (
        <Box>
          <Typography variant="subtitle2">{formatMessage("earnings.reconciliation")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatMessage("earnings.reconciliationDays", { values: { days: summary.totals.comparedDays } })}:{" "}
            {formatMessage("earnings.ourEstimate")} {formatPln(intl, summary.totals.oursOnComparedDays ?? 0)} PLN
            {" · "}
            {formatMessage("earnings.tradeaReport")} {formatPln(intl, summary.totals.tradeaPln)} PLN
            {" · "}
            {formatMessage("earnings.difference")}{" "}
            <Box component="span" fontWeight={600} color="text.primary">
              {(summary.totals.diffPln ?? 0) > 0 ? "+" : ""}
              {formatPln(intl, summary.totals.diffPln ?? 0)} PLN
              {summary.totals.diffPct !== null
                ? ` (${summary.totals.diffPct > 0 ? "+" : ""}${summary.totals.diffPct}%)`
                : ""}
            </Box>
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {formatMessage("earnings.noReportYet")}
        </Typography>
      )}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {formatMessage("earnings.summaryChartTitle")}
        </Typography>
        <SummaryChart summary={summary} />
      </Box>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {formatMessage("earnings.summaryTableTitle")}
        </Typography>
        <SummaryTable summary={summary} />
      </Box>
    </Stack>
  );
};

// Shadow-mode curtailment for the day: what the rule would have done each
// quarter and what that would have been worth. Only quarters the rule
// flagged or that turned out negative are listed.
const ShadowCurtailment = ({ date }: { date: Date }) => {
  const formatMessage = useFormatMessage();
  const intl = useIntl();
  const { data } = useGetCurtailment(date);
  // The coming-hours strip belongs to today only; other days are history.
  const today = isToday(date);
  const { data: strategy } = useGetStrategy();
  const timezone = useAppSelector(getSelectedPowerPlant)?.timezone || "Europe/Warsaw";
  if (!data || !data.configured) return null;
  const t = data.totals;
  // Every quarter the simulation had an opinion about: it said "off", it saw a
  // risk (amber on the strip, which still means the plant runs), or the price
  // turned out negative while the plant was producing or standing idle.
  const rows = data.quarters.filter(
    (q) => q.curtail || (q.probNegative ?? 0) >= 0.3 || (q.cen !== null && q.cen < 0 && ((q.producedKwh ?? 0) > 0 || q.idle)),
  );
  // What the decision turned out to be worth, once the price is known.
  const outcome = (q: CurtailmentQuarter): { key: string; color: "success" | "error" | "warning" | "default" } | null => {
    if (q.cen === null) return null;
    const negative = q.cen < 0;
    if (q.curtail) return negative ? { key: "strategy.outcomeHit", color: "success" } : { key: "strategy.outcomeFalseAlarm", color: "error" };
    if (negative) return (q.producedKwh ?? 0) > 0 || q.idle ? { key: "strategy.outcomeMiss", color: "warning" } : null;
    return { key: "strategy.outcomeOk", color: "default" };
  };
  return (
    <Box>
      <Typography variant="subtitle2" mb={1}>
        {formatMessage("strategy.shadowTitle")}
      </Typography>
      {today && strategy?.configured && (
        <Box mb={2}>
          <CurtailmentOutlook outlook={strategy.outlook} legend timezone={timezone} />
        </Box>
      )}
      {t.decided === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {formatMessage("strategy.noDecisions")}
        </Typography>
      ) : (
        <Stack gap={1.5}>
          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-end">
            <Summary label={formatMessage("strategy.simResult")} value={t.gainPln} primary />
            <Summary label={formatMessage("strategy.possible")} value={t.potentialPln} />
            <Stack minWidth={0}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5}>
                {formatMessage("strategy.decision")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatMessage("strategy.simBreakdown", {
                  values: {
                    hits: t.hits, hitGain: `+${formatPln(intl, t.hitGainPln)}`,
                    fa: t.falseAlarms, faCost: formatPln(intl, t.falseAlarmPln),
                    misses: t.misses, missed: formatPln(intl, t.missedPln),
                  },
                })}
                {t.lostKwh > 0 ? ` · ${formatMessage("strategy.lostKwh", { values: { kwh: formatKwh(intl, t.lostKwh) } })}` : ""}
              </Typography>
            </Stack>
          </Stack>
          {rows.length > 0 && (
            <StickyTable>
                <TableHead>
                  <TableRow sx={{ "& th": { whiteSpace: "nowrap" } }}>
                    <TableCell>{formatMessage("strategy.quarter")}</TableCell>
                    <TableCell>{formatMessage("strategy.decision")}</TableCell>
                    <TableCell>{formatMessage("strategy.outcome")}</TableCell>
                    <TableCell align="right" sx={quiet}>{formatMessage("strategy.pseForecast")}</TableCell>
                    <TableCell align="right" sx={quiet}>{formatMessage("strategy.pNegative")}</TableCell>
                    <TableCell align="right" sx={quiet}>{formatMessage("strategy.pseActual")}</TableCell>
                    <TableCell align="right" sx={quiet}>{formatMessage("earnings.production")} kWh</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMessage("strategy.result")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((q, i) => (
                    <TableRow key={`${i}-${q.time}`}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{q.time}</TableCell>
                      <TableCell>
                        {/* Amber "ryzyko" is still a run decision — the rule did not fire. */}
                        <Chip
                          size="small"
                          variant="outlined"
                          color={q.curtail ? "error" : (q.probNegative ?? 0) >= 0.3 ? "warning" : "default"}
                          label={formatMessage(q.curtail ? "strategy.off" : (q.probNegative ?? 0) >= 0.3 ? "strategy.riskOn" : "strategy.on")}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {(() => {
                          const o = outcome(q);
                          return o ? (
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color={o.color === "default" ? "text.secondary" : `${o.color}.main`}
                            >
                              {formatMessage(o.key as Parameters<typeof formatMessage>[0])}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right" sx={quiet}>{money(intl, q.p50)}</TableCell>
                      <TableCell align="right" sx={quiet}>{q.probNegative === null ? "—" : `${Math.round(q.probNegative * 100)}%`}</TableCell>
                      <TableCell align="right" sx={quiet}>
                        <Box component="span" sx={q.cen !== null && !q.cenSettled ? { fontStyle: "italic" } : undefined}>{money(intl, q.cen)}</Box>
                      </TableCell>
                      <TableCell align="right" sx={quiet}>
                        {q.producedKwh === null
                          ? "—"
                          : q.idle
                            ? formatMessage("strategy.followedKwh", { values: { kwh: formatKwh(intl, q.expectedKwh ?? 0) } })
                            : formatKwh(intl, q.producedKwh)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: (q.gainPln ?? 0) < 0 ? "error.main" : undefined }}>
                        {money(intl, q.gainPln)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
            </StickyTable>
          )}
          <Typography variant="caption" color="text.secondary">{formatMessage("strategy.shadowHint")}</Typography>
        </Stack>
      )}
    </Box>
  );
};

const DayView = ({ date }: { date: Date }) => {
  const formatMessage = useFormatMessage();
  const theme = useTheme();
  const { pse: pseColor } = priceSeriesColors(theme.palette.mode);
  const { data, isLoading } = useGetEarnings(date);
  const day = data?.configured ? data : null;
  if (isLoading) return <Loading />;
  if (!day) {
    return (
      <Typography color="text.secondary" textAlign="center" py={6}>
        {formatMessage("prices.noData")}
      </Typography>
    );
  }
  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        {/* Gated on the contract: a fixed-price day (PV Dom since 2025-05) has
            no index, and `index !== "FIX1"` used to label it "PSE (CRO)". */}
        {day.mode === "market" && day.index !== null && (
          <Chip
            size="small"
            label={`${formatMessage("earnings.settlement")}: ${formatMessage(
              day.index === "FIX1" ? "earnings.indexFix1" : "earnings.indexCro",
            )}`}
          />
        )}
        <Chip
          size="small"
          variant="outlined"
          label={formatMessage(day.settled ? "earnings.settled" : "earnings.estimate")}
        />
        {day.mode === "market" && !day.hasForecast && (
          <Chip size="small" color="warning" variant="outlined" label={formatMessage("earnings.noForecast")} />
        )}
        {day.fix1Source === "tge-hourly" && (
          <Chip size="small" color="warning" variant="outlined" label={formatMessage("earnings.fix1Fallback")} />
        )}
      </Stack>

      <Box sx={tileGridSx}>
        <Summary label={formatMessage("earnings.ourEstimate")} value={day.totals.totalPln} primary />
        {day.totals.tradeaPln !== null && (
          <Summary label={formatMessage("earnings.tradeaReport")} value={day.totals.tradeaPln} primary />
        )}
        <Summary label={formatMessage("earnings.production")} value={day.totals.producedKwh} unit="kWh" primary />
        <Summary label={formatMessage("earnings.rdnPart")} value={day.totals.rdnPln} color={theme.palette.primary.main} />
        <Summary label={formatMessage("earnings.psePart")} value={day.totals.psePln} color={pseColor} />
      </Box>
      <Box sx={tileGridSx}>
        <Summary
          label={formatMessage(day.index === "CRO" ? "earnings.tradeaPoKwh" : "earnings.tradeaForecastKwh")}
          value={day.totals.forecastKwh}
          unit="kWh"
        />
        <Summary label={formatMessage("earnings.ourForecastKwh")} value={day.totals.ourForecastKwh} unit="kWh" />
        <Summary
          label={formatMessage("earnings.tradeaForecastPln")}
          value={day.projectedPln ?? day.totals.expectedTradeaPln}
        />
        <Summary
          label={formatMessage("earnings.ourForecastPln")}
          value={day.projectedOursPln ?? day.totals.expectedOursPln}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {formatMessage("earnings.chartTitle")}
        </Typography>
        <EarningsChart day={day} />
      </Box>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {formatMessage("earnings.tableTitle")}
        </Typography>
        <EarningsTable day={day} />
      </Box>
      <ShadowCurtailment date={date} />
    </Stack>
  );
};

const EarningsContent = () => {
  const { dateType, date } = useFiltersContext();
  const type: ChartDateType = dateType[0];
  return (
    <Card variant="outlined">
      <CardContent>
        {type === "day" ? <DayView date={date.day} /> : <SummaryView grouping={type} date={date[type]} />}
      </CardContent>
    </Card>
  );
};

export const EarningsPage = () => {
  const formatMessage = useFormatMessage();
  return (
    <FiltersContextProvider initialState={{ dateType: ["day"] }}>
      <PageLayout pageTitle={formatMessage("pageTitle.earnings")}>
        <Stack gap={3}>
          <EarningsWidget disableLink />
          <FiltersWidget expandable={false}>
            <DatePickerChips />
          </FiltersWidget>
          <EarningsContent />
        </Stack>
      </PageLayout>
    </FiltersContextProvider>
  );
};
